import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'preact/hooks';

import { Button } from '@/components/Buttons';
import { getProductsQueryOptions } from '@/queries/products';
import { Product, ProductInterval } from '@/queries/products/types';
import { useSelector } from '@/redux/helper';
import { trackMP } from '@/shared/mp';

import { useCheckoutInterception } from './CheckoutHandlerContext';
import ProductCard, { type CardFace, faceComponentName, useProductCard } from './ProductCard';
import ProductEligibilityResult from './ProductEligibilityResult';
import ProductIntervalSelector from './ProductIntervalSelector';
import ProductPrice from './ProductPrice';
import useEligibilityBlocked from './useEligibilityBlocked';
import useProductCheckout from './useProductCheckout';

interface SponsorshipSubscriptionProps {
  habitatId: string;
  entryPoint?: string; // for analytics only
}

/**
 * Which product the what's-included bullets are read from, whatever the visitor has selected.
 *
 * One fixed product rather than the chosen tier, so the panel says the same thing however the
 * visitor arrives at it — the six sponsorship products are one offer at three prices, and a list
 * that changed as you toggled Monthly/Yearly would read as six different offers. Tier 1 because it
 * is the first one rendered and the cheapest way in, and because naming it means only one Stripe
 * product needs `benefits` metadata rather than six.
 */
const BENEFITS_SOURCE_LOOKUP_KEY = 'monthly-supporter-tier-1';

const getProductTitle = (productLookupKey: string | undefined) => {
  switch (productLookupKey) {
    case 'monthly-supporter-tier-1':
    case 'annual-supporter-tier-1':
      return 'HELPER';
    case 'monthly-supporter-tier-2':
    case 'annual-supporter-tier-2':
      return 'FRIEND';
    case 'monthly-supporter-tier-3':
    case 'annual-supporter-tier-3':
      return 'GUARDIAN';
    default:
      return null;
  }
};

const getProductColor = (productLookupKey: string | undefined) => {
  switch (productLookupKey) {
    case 'monthly-supporter-tier-1':
    case 'annual-supporter-tier-1':
      return 'aria-checked:bg-[#FBFFCA] aria-checked:border-[#CDD565]';
    case 'monthly-supporter-tier-2':
    case 'annual-supporter-tier-2':
      return 'aria-checked:bg-[#FFF2F8] aria-checked:border-[#FFDCEE]';
    case 'monthly-supporter-tier-3':
    case 'annual-supporter-tier-3':
      return 'aria-checked:bg-[#FFE1CC] aria-checked:border-[#FFB682]';
    default:
      return null;
  }
};

interface SponsorshipCheckoutButtonProps {
  face: CardFace;
  product: Product | null;
  disabled: boolean;
  loading: boolean;
  onCheckout: () => void;
}

/**
 * The purchase button, on either face.
 *
 * On the front an unchosen tier leaves nothing to buy, so the button is disabled. Inside the panel
 * it stays live and goes back to the front instead: someone who opened the panel to decide should
 * not be sent to a dead control, and the tiers are on the other face.
 *
 * Must be a component rather than a fragment built in the card body, because the flip state it reads
 * only exists below `ProductCard.Root`.
 */
const SponsorshipCheckoutButton = ({ face, product, disabled, loading, onCheckout }: SponsorshipCheckoutButtonProps) => {
  const { showWhatsIncluded, setShowWhatsIncluded } = useProductCard();
  const needsSelection = !product;
  return (
    <Button
      data-component={faceComponentName(face, 'checkout-button')}
      /*
       * Falling through to `disabled` would re-disable the panel's copy, because `!product` is
       * exactly the condition that put us in this branch.
       */
      disabled={needsSelection ? !showWhatsIncluded : disabled}
      loading={loading}
      size="lg"
      className="w-full"
      onClick={() => {
        if (needsSelection) {
          setShowWhatsIncluded(false);
          return;
        }
        onCheckout();
      }}
    >
      {needsSelection ? 'Select an option' : 'Support Now'}
    </Button>
  );
};

const SponsorshipSubscription = ({ habitatId, entryPoint }: SponsorshipSubscriptionProps) => {
  const habitat = useSelector((state) => state.allHabitats.find((h) => h._id === habitatId));
  const [productInterval, setProductInterval] = useState<ProductInterval>('month');
  const [product, setProduct] = useState<Product | null>(null);

  const { data: sponsorshipProducts } = useQuery({
    ...getProductsQueryOptions({
      habitatId,
      lookupKey:
        'monthly-supporter-tier-1,monthly-supporter-tier-2,monthly-supporter-tier-3,annual-supporter-tier-1,annual-supporter-tier-2,annual-supporter-tier-3',
    }),
    select: (data) => {
      const map = data.list.reduce((acc, product) => {
        if (!product.productLookupKey) return acc;
        acc.set(product.productLookupKey, product);
        return acc;
      }, new Map<string, Product>());
      return {
        byInterval: {
          month: [
            map.get('monthly-supporter-tier-1'),
            map.get('monthly-supporter-tier-2'),
            map.get('monthly-supporter-tier-3'),
          ].filter((product) => product != null),
          year: [
            map.get('annual-supporter-tier-1'),
            map.get('annual-supporter-tier-2'),
            map.get('annual-supporter-tier-3'),
          ].filter((product) => product != null),
        } as Partial<Record<ProductInterval, Product[]>>,
        /** Read once, from a fixed tier — see `BENEFITS_SOURCE_LOOKUP_KEY`. */
        benefitsSource: map.get(BENEFITS_SOURCE_LOOKUP_KEY),
      };
    },
  });

  const products = sponsorshipProducts?.byInterval[productInterval];
  const eligibilityBlocked = useEligibilityBlocked(product);
  const { start: startCheckout, isPending } = useProductCheckout();

  /**
   * Restore a tier the visitor already chose once — they picked it, went to Stripe, and came back
   * without paying. Both the tier and the monthly/yearly toggle come from the product itself, so
   * this puts the card back exactly as they left it rather than at "Select an option".
   *
   * Runs only until they touch anything: `product` becomes non-null and the effect stops matching.
   */
  const previousSelection = useCheckoutInterception()?.previousSelection;
  /**
   * Which previous selection has already been put back. Without it this restores forever: switching
   * the billing interval clears `product` by design, which is the very condition the effect waits
   * for, so it immediately reinstated the old tier *and* the old interval — leaving the monthly and
   * yearly toggle looking broken to anyone who had been to Stripe and come back.
   */
  const restoredSelectionIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    const previousProductId = previousSelection?.productId;
    if (product || !previousProductId || !sponsorshipProducts || restoredSelectionIdRef.current === previousProductId) {
      return;
    }
    restoredSelectionIdRef.current = previousProductId;
    const entries = Object.entries(sponsorshipProducts.byInterval) as [ProductInterval, Product[]][];
    // eslint-disable-next-line no-restricted-syntax
    for (const [interval, list] of entries) {
      const match = list.find((p) => p._id === previousProductId);
      if (match) {
        setProductInterval(interval);
        setProduct(match);
        break;
      }
    }
  }, [previousSelection?.productId, sponsorshipProducts, product]);

  if (!habitat) return null;

  const trackedProps = { entryPoint, productLookupKey: product?.productLookupKey ?? BENEFITS_SOURCE_LOOKUP_KEY, habitatId };

  const renderCheckoutButton = (face: CardFace) => (
    <SponsorshipCheckoutButton
      face={face}
      product={product}
      disabled={!product || isPending || eligibilityBlocked}
      loading={isPending}
      onCheckout={() => {
        if (!product || !habitat) return;
        startCheckout({
          productId: product._id,
          priceId: product.price.priceId,
          // Generic product: the habitat being sponsored is attached here, per checkout.
          associatedHabitatIds: [habitatId],
          successPath: `/h/${habitat.zoo.slug}/${habitat.slug}?tab=sponsors&sponsored=1`,
          cancelPath: `/h/${habitat.zoo.slug}/${habitat.slug}?ppt=sponsor`,
          productLookupKey: product.productLookupKey,
          entryPoint,
        });
      }}
    />
  );

  const eligibilityLine = product && !product.eligibility?.tierChangeableUserProductId && (
    <ProductEligibilityResult result={product.eligibility} />
  );

  return (
    <ProductCard.Root data-component="sponsorship">
      <ProductCard.Front className="gap-[13px] pb-6">
        <div className="relative h-[104px] w-full rounded-t-2xl bg-gray-400">
          {habitat.backgroundImage && (
            <img
              src={habitat.backgroundImage}
              alt={habitat.title}
              className="preflight preflight-img size-full overflow-hidden rounded-t-2xl object-cover"
            />
          )}
          <div className="absolute inset-x-0 -bottom-7 flex items-center justify-center">
            <div className="relative">
              {habitat.profileImage ? (
                <img
                  src={habitat.profileImage}
                  alt={habitat.title}
                  className="preflight preflight-img size-[108px] overflow-hidden rounded-full border border-white object-cover"
                />
              ) : (
                <div className="preflight flex size-[108px] items-center justify-center overflow-hidden rounded-full border border-white bg-gray-400 text-2xl font-bold text-white">
                  {habitat.title.charAt(0)}
                </div>
              )}
              {habitat.zoo && (
                <div className="absolute bottom-0 right-0 rounded-full bg-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]">
                  <img
                    src={habitat.zoo.logo}
                    alt={habitat.zoo.name}
                    className="preflight preflight-img size-[44px] overflow-hidden rounded-full object-contain"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="w-full px-4 pt-5">
          <h4 className="preflight preflight-h4 text-center text-2xl/none font-bold text-green-900">Habitat Supporter</h4>
          <p className="preflight preflight-p mt-3 text-center text-base/6 text-stone-700">
            Unlimited access to <span className="font-bold">{habitat?.title ?? 'this habitat'}</span> livestream
          </p>
          <p className="preflight preflight-p mt-2 text-center text-sm/5 text-stone-500">
            Choose your contribution level, and follow their story
          </p>
        </div>

        <ProductIntervalSelector
          value={productInterval}
          onChange={(interval) => {
            if (interval === productInterval) return;
            setProduct(null);
            setProductInterval(interval);
          }}
        />

        <div
          role="radiogroup"
          aria-label="Sponsorship tier"
          className="my-2 grid w-full gap-2 px-2"
          style={{ gridTemplateColumns: `repeat(${products?.length ?? 0}, 1fr)` }}
        >
          {products?.map((p) => (
            <button
              role="radio"
              type="button"
              aria-checked={p._id === product?._id}
              className={clsx(
                'preflight preflight-button flex h-20 flex-col items-center justify-center gap-2 rounded-2xl border-2 border-[#CFDEAC] px-0.5 py-3.5 text-secondary-green',
                getProductColor(p.productLookupKey),
              )}
              key={p._id}
              onClick={() => setProduct(p)}
            >
              <h6 className="preflight preflight-h6 text-center text-base font-bold text-secondary-green">
                {getProductTitle(p.productLookupKey)}
              </h6>
              <span className="text-lg/none">
                <ProductPrice
                  currency={p.price.currency}
                  price={p.price.price}
                  className="font-normal leading-none [font-size:inherit]"
                />
                /{productInterval === 'month' ? 'Mo' : 'Yr'}
              </span>
            </button>
          ))}
        </div>

        <div className="flex w-full flex-col items-center gap-3 px-4">
          {renderCheckoutButton('front')}
          {eligibilityLine}
          <ProductCard.WhatsIncludedTrigger onOpen={() => trackMP('click-whats-included', trackedProps)} />
        </div>
      </ProductCard.Front>

      <ProductCard.WhatsIncluded>
        <ProductCard.BackButton />
        <ProductCard.WhatsIncludedHeading>
          {productInterval === 'month' ? 'Monthly' : 'Yearly'} unlimited access to an animal you love. Follow their story
          live
        </ProductCard.WhatsIncludedHeading>
        <ProductCard.Benefits product={sponsorshipProducts?.benefitsSource} />
        <ProductCard.CheckoutGroup>
          {renderCheckoutButton('whats-included')}
          {eligibilityLine}
        </ProductCard.CheckoutGroup>
      </ProductCard.WhatsIncluded>
    </ProductCard.Root>
  );
};

export default SponsorshipSubscription;
